import { apiRequest } from './api';
import { ApiResponse } from '../types';
import {
  Veiculo,
  VeiculoMulta,
  RequisicaoAbastecimento,
  ManutencaoVeiculo,
  TrocaOleo,
  VeiculoEvento,
  VeiculoHistoricoItem,
  ApuracaoResumo,
  AlertaTrocaOleo,
  TIPOS_COMBUSTIVEL,
  TipoCombustivel,
} from '../types/frota';

class FrotaService {
  // Veículos
  async listarVeiculos(filters: Record<string, any> = {}): Promise<ApiResponse<Veiculo[]>> {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => {
      if (v != null && v !== '') params.append(k, String(v));
    });
    return apiRequest.get(`/frota/veiculos?${params}`);
  }

  async getAlertasTrocaOleo(limiteKm = 100): Promise<AlertaTrocaOleo[]> {
    const res = await apiRequest.get<AlertaTrocaOleo[]>(`/frota/alertas-troca-oleo?limite_km=${limiteKm}`);
    return res.data ?? [];
  }

  async obterVeiculo(id: number): Promise<ApiResponse<Veiculo>> {
    return apiRequest.get(`/frota/veiculos/${id}`);
  }

  async criarVeiculo(data: FormData): Promise<ApiResponse<Veiculo>> {
    return apiRequest.post('/frota/veiculos', data);
  }

  async atualizarVeiculo(id: number, data: FormData): Promise<ApiResponse<Veiculo>> {
    return apiRequest.put(`/frota/veiculos/${id}`, data);
  }

  async excluirVeiculo(id: number): Promise<ApiResponse<void>> {
    return apiRequest.delete(`/frota/veiculos/${id}`);
  }

  // Multas
  async listarMultas(veiculoId: number): Promise<ApiResponse<VeiculoMulta[]>> {
    return apiRequest.get(`/frota/veiculos/${veiculoId}/multas`);
  }

  async criarMulta(data: Partial<VeiculoMulta>): Promise<ApiResponse<VeiculoMulta>> {
    return apiRequest.post('/frota/multas', data);
  }

  async atualizarMulta(id: number, data: Partial<VeiculoMulta>): Promise<ApiResponse<VeiculoMulta>> {
    return apiRequest.put(`/frota/multas/${id}`, data);
  }

  async excluirMulta(id: number): Promise<ApiResponse<void>> {
    return apiRequest.delete(`/frota/multas/${id}`);
  }

  // Abastecimentos
  async listarAbastecimentos(filters: Record<string, any> = {}): Promise<ApiResponse<RequisicaoAbastecimento[]>> {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => {
      if (v != null && v !== '') params.append(k, String(v));
    });
    return apiRequest.get(`/frota/abastecimentos?${params}`);
  }

  async criarAbastecimento(data: Partial<RequisicaoAbastecimento>): Promise<ApiResponse<RequisicaoAbastecimento>> {
    return apiRequest.post('/frota/abastecimentos', data);
  }

  async atualizarAbastecimento(id: number, data: Partial<RequisicaoAbastecimento>): Promise<ApiResponse<RequisicaoAbastecimento>> {
    return apiRequest.put(`/frota/abastecimentos/${id}`, data);
  }

  async aprovarAbastecimento(id: number, aprovado: boolean, observacoes?: string): Promise<ApiResponse<RequisicaoAbastecimento>> {
    return apiRequest.post(`/frota/abastecimentos/${id}/aprovar`, { aprovado, observacoes });
  }

  async resumoApuracao(mes_referencia: string): Promise<ApiResponse<ApuracaoResumo>> {
    return apiRequest.get(`/frota/abastecimentos/apuracao/resumo?mes_referencia=${mes_referencia}`);
  }

  async apurarAbastecimentos(data: { mes_referencia: string; escritorio_id: number; data_vencimento?: string; observacoes?: string }): Promise<ApiResponse<any>> {
    return apiRequest.post('/frota/abastecimentos/apuracao', data);
  }

  // Manutenções
  async listarManutencoes(filters: Record<string, any> = {}): Promise<ApiResponse<ManutencaoVeiculo[]>> {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => {
      if (v != null && v !== '') params.append(k, String(v));
    });
    return apiRequest.get(`/frota/manutencoes?${params}`);
  }

  async criarManutencao(data: Partial<ManutencaoVeiculo>): Promise<ApiResponse<ManutencaoVeiculo>> {
    return apiRequest.post('/frota/manutencoes', data);
  }

  async atualizarManutencao(id: number, data: Partial<ManutencaoVeiculo>): Promise<ApiResponse<ManutencaoVeiculo>> {
    return apiRequest.put(`/frota/manutencoes/${id}`, data);
  }

  async excluirManutencao(id: number): Promise<ApiResponse<void>> {
    return apiRequest.delete(`/frota/manutencoes/${id}`);
  }

  // Trocas de óleo
  async listarTrocasOleo(filters: Record<string, any> = {}): Promise<ApiResponse<TrocaOleo[]>> {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => {
      if (v != null && v !== '') params.append(k, String(v));
    });
    return apiRequest.get(`/frota/trocas-oleo?${params}`);
  }

  async criarTrocaOleo(data: Partial<TrocaOleo>): Promise<ApiResponse<TrocaOleo>> {
    return apiRequest.post('/frota/trocas-oleo', data);
  }

  async atualizarTrocaOleo(id: number, data: Partial<TrocaOleo>): Promise<ApiResponse<TrocaOleo>> {
    return apiRequest.put(`/frota/trocas-oleo/${id}`, data);
  }

  async excluirTrocaOleo(id: number): Promise<ApiResponse<void>> {
    return apiRequest.delete(`/frota/trocas-oleo/${id}`);
  }

  // Eventos de veículo
  async listarEventos(filters: Record<string, any> = {}): Promise<ApiResponse<VeiculoEvento[]>> {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => {
      if (v != null && v !== '') params.append(k, String(v));
    });
    return apiRequest.get(`/frota/eventos?${params}`);
  }

  async obterEvento(id: number): Promise<ApiResponse<VeiculoEvento>> {
    return apiRequest.get(`/frota/eventos/${id}`);
  }

  async criarEvento(data: FormData): Promise<ApiResponse<VeiculoEvento>> {
    return apiRequest.post('/frota/eventos', data);
  }

  async atualizarEvento(id: number, data: FormData): Promise<ApiResponse<VeiculoEvento>> {
    return apiRequest.put(`/frota/eventos/${id}`, data);
  }

  async excluirEvento(id: number): Promise<ApiResponse<void>> {
    return apiRequest.delete(`/frota/eventos/${id}`);
  }

  async listarHistoricoVeiculo(veiculoId: number): Promise<VeiculoHistoricoItem[]> {
    const [eventosRes, abastRes, manutRes, trocasRes, multasRes] = await Promise.all([
      this.listarEventos({ veiculo_id: veiculoId, limit: 500 }),
      this.listarAbastecimentos({ veiculo_id: veiculoId, limit: 500 }),
      this.listarManutencoes({ veiculo_id: veiculoId, limit: 500 }),
      this.listarTrocasOleo({ veiculo_id: veiculoId, limit: 500 }),
      this.listarMultas(veiculoId),
    ]);

    const items: VeiculoHistoricoItem[] = [];

    (eventosRes.data || []).forEach((e) => {
      items.push({
        id: `evento-${e.id}`,
        categoria: 'evento',
        data: e.data_evento,
        titulo: this.tipoEventoLabel(e.tipo),
        detalhes: [
          e.nivel_combustivel ? `Tanque: ${this.nivelCombustivelLabel(e.nivel_combustivel)}` : null,
          e.observacoes || null,
          e.anexos?.length ? `${e.anexos.length} foto(s)` : null,
        ].filter(Boolean).join(' • ') || '-',
        km: e.kilometragem,
        motorista: e.motorista?.nome,
        motorista_id: e.funcionario_id,
        anexos: e.anexos,
        raw: e,
      });
    });

    (abastRes.data || []).forEach((a) => {
      items.push({
        id: `abastecimento-${a.id}`,
        categoria: 'abastecimento',
        data: a.data_requisicao,
        titulo: 'Abastecimento',
        detalhes: `${this.tipoCombustivelLabel(a.tipo_combustivel)} • ${this.formatQuantidadeLitros(Number(a.quantidade_litros))} • ${this.formatCurrency(Number(a.valor))}`,
        km: a.kilometragem_atual,
        motorista: a.funcionario_solicitante?.nome,
        motorista_id: a.funcionario_solicitante_id,
        valor: Number(a.valor),
        status: this.statusAbastecimentoLabel(a.status),
        raw: a,
      });
    });

    (manutRes.data || []).forEach((m) => {
      items.push({
        id: `manutencao-${m.id}`,
        categoria: 'manutencao',
        data: m.data_manutencao,
        titulo: `Manutenção (${m.tipo})`,
        detalhes: m.problema || m.descricao_servico || m.mecanica || '-',
        km: m.kilometragem,
        motorista: m.funcionario_solicitante?.nome,
        motorista_id: m.funcionario_solicitante_id,
        valor: m.valor ? Number(m.valor) : null,
        status: this.statusManutencaoLabel(m.status),
        raw: m,
      });
    });

    (trocasRes.data || []).forEach((t) => {
      items.push({
        id: `troca_oleo-${t.id}`,
        categoria: 'troca_oleo',
        data: t.data_troca,
        titulo: 'Troca de óleo',
        detalhes: [t.tipo_oleo, t.mecanica, t.proxima_troca_km ? `Próxima: ${t.proxima_troca_km} km` : null].filter(Boolean).join(' • ') || '-',
        km: t.kilometragem,
        valor: t.valor ? Number(t.valor) : null,
        raw: t,
      });
    });

    (multasRes.data || []).forEach((m) => {
      items.push({
        id: `multa-${m.id}`,
        categoria: 'multa',
        data: m.data_multa,
        titulo: m.infracao,
        detalhes: [
          m.pontos ? `${m.pontos} ponto(s)` : null,
          m.data_vencimento ? `Venc.: ${m.data_vencimento}` : null,
          m.observacoes || null,
        ].filter(Boolean).join(' • ') || '-',
        motorista: m.motorista?.nome,
        motorista_id: m.funcionario_id,
        valor: Number(m.valor),
        status: this.statusMultaLabel(m.status),
        raw: m,
      });
    });

    return items.sort((a, b) => b.data.localeCompare(a.data));
  }

  categoriaHistoricoLabel(categoria: string): string {
    const map: Record<string, string> = {
      evento: 'Evento',
      abastecimento: 'Abastecimento',
      manutencao: 'Manutenção',
      troca_oleo: 'Troca de óleo',
      multa: 'Multa',
    };
    return map[categoria] || categoria;
  }

  statusMultaLabel(status: string): string {
    const map: Record<string, string> = {
      pendente: 'Pendente',
      paga: 'Paga',
      contestada: 'Contestada',
    };
    return map[status] || status;
  }

  tipoEventoLabel(tipo: string): string {
    const map: Record<string, string> = {
      entrega: 'Entrega',
      devolucao: 'Devolução',
      outro: 'Outro',
    };
    return map[tipo] || tipo;
  }

  nivelCombustivelLabel(nivel?: string | null): string {
    if (!nivel) return '-';
    const map: Record<string, string> = {
      vazio: 'Vazio',
      '1/4': '1/4',
      '1/2': '1/2',
      '3/4': '3/4',
      cheio: 'Cheio',
    };
    return map[nivel] || nivel;
  }

  getUploadUrl(path: string): string {
    if (!path) return '';
    if (path.startsWith('http')) return path;
    const base = import.meta.env.VITE_API_URL || 'http://localhost:3001';
    return `${base}${path}`;
  }

  formatCurrency(value: number): string {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
  }

  statusAbastecimentoLabel(status: string): string {
    const map: Record<string, string> = {
      pendente: 'Pendente',
      aprovada: 'Aprovada',
      rejeitada: 'Rejeitada',
      apurada: 'Apurada',
      paga: 'Paga',
    };
    return map[status] || status;
  }

  tipoCombustivelLabel(tipo: TipoCombustivel | string): string {
    return TIPOS_COMBUSTIVEL.find((t) => t.value === tipo)?.label || tipo;
  }

  calcularQuantidadeLitros(valor: number, valorLitro: number): number {
    if (!valorLitro || valorLitro <= 0 || !valor || valor <= 0) return 0;
    return Math.round((valor / valorLitro) * 1000) / 1000;
  }

  formatQuantidadeLitros(value: number): string {
    return `${(value || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} L`;
  }

  calcularEstatisticasAbastecimento(params: {
    kilometragem_atual: number;
    quantidade_litros: number;
    media_consumo_km_litro?: number | null;
  }) {
    const media = Number(params.media_consumo_km_litro) || 0;
    const litros = params.quantidade_litros || 0;
    const kmAtual = params.kilometragem_atual || 0;
    const kmComAbastecimento = media > 0 && litros > 0
      ? Math.round(media * litros)
      : 0;
    const proximaKilometragem = kmComAbastecimento > 0
      ? kmAtual + kmComAbastecimento
      : 0;

    return { media, kmComAbastecimento, proximaKilometragem };
  }

  statusManutencaoLabel(status: string): string {
    const map: Record<string, string> = {
      pendente: 'Pendente',
      agendada: 'Agendada',
      em_andamento: 'Em andamento',
      concluida: 'Concluída',
      cancelada: 'Cancelada',
    };
    return map[status] || status;
  }
}

export default new FrotaService();
